import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SetupPortalPage from '../../components/setup/SetupPortalPage';
import BackToAgentSetupLink from '../../components/setup/BackToAgentSetupLink';
import AgentCard from '../../components/agents/AgentCard';
import AgentForm from '../../components/agents/AgentForm';
import AgentCreationWizard from '../../components/agents/AgentCreationWizard';
import api from '../../API.js';
import styles from '../../styles/agents.module.css';

function AgentManagement() {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('agent', { query: { activeOnly: false } });
            
            if (response.status === 404) {
                setError('Agent management is not available on this server.');
                return;
            }
            
            if (response.status !== 200) {
                throw new Error(`Failed to fetch agents: ${response.status}`);
            }
            setAgents(response.data.data || response.data);
        } catch (err) {
            console.error('Error fetching agents:', err);
            if (err.message.includes('404') || err.message.includes('Not Found')) {
                setError('Agent management is not available on this server.');
            } else {
                setError('Failed to load agents: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAgent = async (formData) => {
        try {
            const response = await api.post('agent', { body: formData });
            
            if (response.status !== 200) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                if (response.data) {
                    if (typeof response.data === 'string') {
                        errorMessage = response.data;
                    } else if (response.data.message) {
                        errorMessage = response.data.message;
                    } else if (response.data.error) {
                        errorMessage = response.data.error;
                    }
                }
                throw new Error(errorMessage);
            }

            await fetchAgents();
            setShowCreateForm(false);
            setShowWizard(false);
        } catch (err) {
            throw err;
        }
    };

    const handleDeleteAgent = async (agentId) => {
        if (!window.confirm(`Are you sure you want to delete agent "${agentId}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await api.delete(`agent/${agentId}`);
            if (response.status === 200) {
                await fetchAgents();
            } else {
                throw new Error(`Failed to delete agent: ${response.status}`);
            }
        } catch (err) {
            console.error('Error deleting agent:', err);
            alert('Failed to delete agent: ' + err.message);
        }
    };

    const filteredAgents = agents.filter(agent => {
        // Filter by tab
        if (activeTab === 'active' && !agent.isActive) return false;
        if (activeTab === 'inactive' && agent.isActive) return false;

        // Filter by search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            return (
                agent.name.toLowerCase().includes(searchLower) ||
                (agent.description && agent.description.toLowerCase().includes(searchLower)) ||
                agent.agentId.toLowerCase().includes(searchLower)
            );
        }

        return true;
    });

    if (showWizard) {
        return (
            <SetupPortalPage>
                <div className={styles.container}>
                    <div style={{ marginBottom: '2em' }}>
                        <BackToAgentSetupLink />
                    </div>
                    <AgentCreationWizard
                        onComplete={async (agent) => {
                            await fetchAgents();
                            setShowWizard(false);
                        }}
                        onCancel={() => setShowWizard(false)}
                    />
                </div>
            </SetupPortalPage>
        );
    }

    if (loading) {
        return (
            <SetupPortalPage>
                <div className={styles.loading}>Loading agents...</div>
            </SetupPortalPage>
        );
    }

    return (
        <SetupPortalPage>
            <div className={styles.container}>
                <div style={{ marginBottom: '2em' }}>
                    <BackToAgentSetupLink />
                </div>

                <div className={styles.header}>
                    <h1>Agent Management</h1>
                    <div className={styles.actions}>
                        <button
                            onClick={() => setShowWizard(true)}
                            className={`${styles.button} ${styles.buttonPrimary}`}
                        >
                            Create Agent (Wizard)
                        </button>
                        <button
                            onClick={() => {
                                setShowCreateForm(!showCreateForm);
                                setShowWizard(false);
                            }}
                            className={`${styles.button} ${styles.buttonSecondary}`}
                        >
                            {showCreateForm ? 'Cancel' : 'Quick Create'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                {showCreateForm && (
                    <div style={{ marginBottom: '3em', padding: '2em', background: '#f6f8fa', borderRadius: '8px', border: '1px solid #e1e4e8' }}>
                        <h2 style={{ marginTop: 0, marginBottom: '1.5em' }}>Create New Agent</h2>
                        <AgentForm
                            onSubmit={handleCreateAgent}
                            onCancel={() => setShowCreateForm(false)}
                        />
                    </div>
                )}

                {!showCreateForm && (
                    <>
                        <div style={{ marginBottom: '1.5em', display: 'flex', gap: '1em', alignItems: 'center' }}>
                            <div className={styles.tabs}>
                                <button
                                    className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
                                    onClick={() => setActiveTab('all')}
                                >
                                    All ({agents.length})
                                </button>
                                <button
                                    className={`${styles.tab} ${activeTab === 'active' ? styles.active : ''}`}
                                    onClick={() => setActiveTab('active')}
                                >
                                    Active ({agents.filter(a => a.isActive).length})
                                </button>
                                <button
                                    className={`${styles.tab} ${activeTab === 'inactive' ? styles.active : ''}`}
                                    onClick={() => setActiveTab('inactive')}
                                >
                                    Inactive ({agents.filter(a => !a.isActive).length})
                                </button>
                            </div>

                            <input
                                type="text"
                                placeholder="Search agents..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.formInput}
                                style={{ flex: 1, maxWidth: '300px' }}
                            />
                        </div>

                        {filteredAgents.length === 0 ? (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateIcon}>🤖</div>
                                <div className={styles.emptyStateTitle}>
                                    {searchTerm ? 'No agents found' : 'No agents yet'}
                                </div>
                                <div className={styles.emptyStateText}>
                                    {searchTerm
                                        ? 'Try adjusting your search terms'
                                        : 'Create your first agent to get started'}
                                </div>
                                {!searchTerm && (
                                    <button
                                        onClick={() => setShowWizard(true)}
                                        className={`${styles.button} ${styles.buttonPrimary}`}
                                        style={{ marginTop: '1em' }}
                                    >
                                        Create Your First Agent
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className={styles.cardsGrid}>
                                {filteredAgents.map(agent => (
                                    <AgentCard
                                        key={agent.agentId}
                                        agent={agent}
                                        onDelete={handleDeleteAgent}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </SetupPortalPage>
    );
}

export default AgentManagement;
